import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Table as TableIcon,
  Mail,
  Phone,
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  User,
  Settings,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { id: "new", name: "New", color: "#3b82f6" },
  { id: "contacted", name: "Contacted", color: "#8b5cf6" },
  { id: "qualified", name: "Qualified", color: "#10b981" },
  { id: "negotiating", name: "Negotiating", color: "#f59e0b" },
  { id: "won", name: "Won", color: "#22c55e" },
  { id: "lost", name: "Lost", color: "#ef4444" }
];

const KanbanPipeline = ({ onViewTable }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  const [visibleStages, setVisibleStages] = useState(
    STAGES.reduce((acc, stage) => ({ ...acc, [stage.id]: true }), {})
  );
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchLeads();
    const interval = setInterval(fetchLeads, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, [leads, visibleStages]);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftScroll(container.scrollLeft > 0);
      setShowRightScroll(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        withCredentials: true
      });
      setLeads(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to fetch leads");
      setLoading(false);
    }
  };

  const getOwnerName = (ownerId) => {
    if (!ownerId) return null;
    const owner = users.find(u => u.id === ownerId);
    if (!owner) return null;
    return owner.first_name && owner.last_name 
      ? `${owner.first_name} ${owner.last_name}` 
      : owner.email.split('@')[0];
  };

  const updateLeadStage = async (leadId, newStage) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { stage: newStage });
      toast.success("Lead moved successfully!");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedLead && draggedLead.stage !== stageId) {
      updateLeadStage(draggedLead.id, stageId);
    }
    setDraggedLead(null);
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.stage === stageId);
  };

  const getVerificationIcon = (status) => {
    switch(status) {
      case 'valid': return <CheckCircle2 className="w-3 h-3 text-emerald-600" />;
      case 'invalid': return <XCircle className="w-3 h-3 text-red-600" />;
      case 'risky': return <AlertCircle className="w-3 h-3 text-amber-600" />;
      default: return <AlertCircle className="w-3 h-3 text-slate-400" />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-slate-400";
  };

  if (loading) {
    return <div className="text-center py-12">Loading pipeline...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="kanban-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Pipeline</h2>
          <p className="text-slate-600 mt-1">Drag and drop leads between stages</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Stage Visibility Toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="toggle-stages-btn"
              >
                <Settings className="w-4 h-4" />
                <span>Show/Hide Stages</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-3">Visible Stages</h4>
                  <div className="space-y-3">
                    {STAGES.map((stage) => (
                      <div key={stage.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`stage-${stage.id}`}
                          checked={visibleStages[stage.id]}
                          onCheckedChange={(checked) => {
                            setVisibleStages(prev => ({
                              ...prev,
                              [stage.id]: checked
                            }));
                          }}
                        />
                        <Label
                          htmlFor={`stage-${stage.id}`}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: stage.color }}
                          />
                          <span>{stage.name}</span>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const allVisible = STAGES.reduce((acc, stage) => ({ ...acc, [stage.id]: true }), {});
                        setVisibleStages(allVisible);
                      }}
                      className="flex-1"
                    >
                      Show All
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const allHidden = STAGES.reduce((acc, stage) => ({ ...acc, [stage.id]: false }), {});
                        setVisibleStages(allHidden);
                      }}
                      className="flex-1"
                    >
                      Hide All
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button 
            onClick={onViewTable}
            variant="outline"
            className="flex items-center space-x-2"
            data-testid="view-table-btn"
          >
            <TableIcon className="w-4 h-4" />
            <span>Table View</span>
          </Button>
        </div>
      </div>

      {/* Kanban Board with Scroll Buttons */}
      <div className="relative">
        {/* Left Scroll Button */}
        {showLeftScroll && (
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-slate-50 transition-colors border border-slate-200"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
        )}

        {/* Right Scroll Button */}
        {showRightScroll && (
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-slate-50 transition-colors border border-slate-200"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        )}

        {/* Scrollable Kanban Container */}
        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-4 scroll-smooth"
          style={{ scrollbarWidth: 'thin' }}
        >
        {STAGES.filter(stage => visibleStages[stage.id]).map((stage) => {
          const stageLeads = getLeadsByStage(stage.id);
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80"
              data-testid={`stage-${stage.id}`}
            >
              {/* Stage Header */}
              <div 
                className="glass-card rounded-xl p-4 mb-4"
                style={{ borderTop: `4px solid ${stage.color}` }}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">{stage.name}</h3>
                  <Badge 
                    className="text-white"
                    style={{ backgroundColor: stage.color }}
                  >
                    {stageLeads.length}
                  </Badge>
                </div>
              </div>

              {/* Drop Zone */}
              <div
                className="space-y-3 min-h-[500px] p-2 rounded-xl bg-slate-50/50"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {stageLeads.map((lead) => (
                  <Card
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead)}
                    onClick={(e) => {
                      // Only navigate if not dragging and not clicking on action buttons
                      if (!e.defaultPrevented) {
                        navigate(`/leads/${lead.id}`);
                      }
                    }}
                    className={`p-4 cursor-pointer hover:shadow-lg transition-all border-l-4 ${
                      draggedLead?.id === lead.id ? 'opacity-50' : ''
                    }`}
                    style={{ borderLeftColor: stage.color }}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    {/* Lead Info */}
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 text-sm">
                            {lead.raw_data?.name || 'Unnamed'}
                          </h4>
                          <p className="text-xs text-slate-600 mt-1">
                            {lead.raw_data?.company || 'No company'}
                          </p>
                          {getOwnerName(lead.owner_id) && (
                            <Badge variant="outline" className="text-xs mt-1">
                              👤 {getOwnerName(lead.owner_id)}
                            </Badge>
                          )}
                        </div>
                        {/* Score Badge */}
                        <div 
                          className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold ${getScoreColor(lead.score_ai)}`}
                        >
                          {Math.round(lead.score_ai)}
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-1.5">
                        {lead.raw_data?.email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-600 truncate">
                              {lead.raw_data.email}
                            </span>
                            {getVerificationIcon(lead.verify_email)}
                          </div>
                        )}
                        {lead.raw_data?.phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-600">
                              {lead.raw_data.phone}
                            </span>
                            {getVerificationIcon(lead.verify_phone)}
                          </div>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex items-center justify-between">
                        {lead.enrichment_status === 'enriched' ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <Sparkles className="w-2.5 h-2.5 mr-1" />
                            Enriched
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            {lead.enrichment_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}

                {stageLeads.length === 0 && (
                  <div className="text-center py-12 text-slate-400 text-sm">
                    Drop leads here
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default KanbanPipeline;
